import { withFileManagerAccess } from "@crm-src/components/page-permission-guard"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import { composeApiErrorMessage } from "@crm-src/lib/api-error"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { PageLoader } from "@/components/ui/page-loader"

import {
  Folder,
  File,
  Upload,
  FolderPlus,
  Search,
  Download,
  Edit,
  Trash2,
  MoreHorizontal,
  Home,
  ChevronRight,
  FileText,
  Eye,
} from "lucide-react"

import { filesApi, type FileItem, type FilesListParams, type BreadcrumbItem } from "@crm-src/lib/files-api"
import { handleApiError } from "@crm-src/lib/error-handler"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { useLatestRequest } from "@crm-src/components/table/useLatestRequest"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import { TableFilterBar } from "@crm-src/components/table/TableFilterBar"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { FILES_TABLE, type TableColumn } from "@crm-src/components/table/configs"
import { buildFileFacetDefs, buildFileListParams, type FileFacetKey } from "./file-manager-page-query"
import {
  isImageFile,
  isPdfFile,
  isVideoFile,
  isAudioFile,
  isCsvFile,
  isTextFile,
  isExcelFile,
  isDocumentFile,
} from "@crm-src/lib/file-type-utils"

// File-manager-specific extracted components. The dialogs and the
// universal file viewer used to live inline in this 2700+ LOC file;
// they were carved out to keep the page focused on table + state.
import { FileTypeIcon } from "@crm-src/components/file-manager/file-type-icon"
import { formatFileDate } from "@crm-src/components/file-manager/format"
import { toggleId, collectIds, allSelected, isIndeterminate } from "@crm-src/lib/selection"
import { UniversalFileViewer } from "@crm-src/components/file-manager/universal-file-viewer"
import { UploadFilesDialog } from "@crm-src/components/file-manager/upload-files-dialog"
import { CreateFolderDialog } from "@crm-src/components/file-manager/create-folder-dialog"
import { EditFileDialog } from "@crm-src/components/file-manager/edit-file-dialog"
import { FileDetailsDialog } from "@crm-src/components/file-manager/file-details-dialog"
import { PageFileViewerDialog } from "@crm-src/components/file-manager/page-file-viewer-dialog"

function FileManagerPage() {
  const { toast } = useToast()

  // State management
  const [files, setFiles] = useState<FileItem[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [currentFolder, setCurrentFolder] = useState<FileItem | null>(null)
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([])

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Annotated, not inferred: TS 5.5 reads a `.filter(...)` callback as a type
  // predicate, so an inferred `facetKeys` would narrow to a different union
  // than the facet defs are built from.
  const facetKeys = useMemo<readonly FileFacetKey[]>(() => FILES_TABLE.facetKeys, [])
  const table = useTableQuery({
    defaultSort: FILES_TABLE.defaultSort,
    defaultDir: FILES_TABLE.defaultDir,
    facetKeys,
  })

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset, which
  // is the part that loops if a page writes it inline. Search text is
  // deliberately NOT in the URL; sort, facets and page are.
  const search = useDebouncedSearch(table.setPage)

  const { users } = useUsers()
  const facetDefs = useMemo(() => buildFileFacetDefs(users), [users])
  // `as const` config entries carry no `align`, so the array needs the explicit
  // element type rather than the literal one TypeScript infers.
  const columns = useMemo<readonly TableColumn[]>(() => FILES_TABLE.columns, [])

  // Modals
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isFileDetailsOpen, setIsFileDetailsOpen] = useState(false)
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null)
  // File viewer states (simplified)
  const [fileViewerErrors, setFileViewerErrors] = useState<Set<string>>(new Set())

  // Form states
  const [uploadFiles, setUploadFiles] = useState<File[]>([])
  const [folderName, setFolderName] = useState("")
  const [editName, setEditName] = useState("")

  // Drag and drop states
  const [isDragging, setIsDragging] = useState(false)

  // Move drag and drop states
  const [draggedItem, setDraggedItem] = useState<FileItem | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)

  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false)

  // Pagination: the REQUESTED page lives in the URL (`table.page`); this is only
  // what the last response reported.
  const [totalCount, setTotalCount] = useState(0)
  const perPage = 50

  // Per-row actions menu anchor (keyed by file id). The separate "Sort" menu
  // anchor is gone: sorting is on the column headers now.
  const [rowMenuAnchor, setRowMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)

  // Clear file viewer errors when needed
  const clearFileViewerErrors = () => {
    setFileViewerErrors(new Set())
  }

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL), the search term from the
  // debounce above, and the folder from the breadcrumb navigation.
  //
  // The shaping lives in `buildFileListParams` (see file-manager-page-query.ts)
  // rather than inline, because what this page does with `sortExplicit` is a
  // DECISION that needed a test: `Api::V1::FilesController` has no
  // search-relevance branch, so dropping `sort`/`dir` during a search would
  // silently reorder the list to name-ascending while the Modified header kept
  // showing its descending arrow. The real `table.sortExplicit` is still
  // threaded through so that one function stays the single place that answers
  // the question.
  //
  // It is generic over the page's facet-key union, so the params type-check
  // against `FilesListParams` field by field: a facet key the API does not
  // implement is a compile error, not a param the server silently drops.
  const listParams = useMemo<FilesListParams>(() => buildFileListParams({
    sort: table.sort,
    dir: table.dir,
    facets: table.facets,
    page: table.page,
    perPage,
    search: search.search,
    sortExplicit: table.sortExplicit,
    ...(currentFolder?.id ? { parentId: currentFolder.id } : {}),
  }), [table.sort, table.dir, table.facets, table.page, table.sortExplicit, search.search, currentFolder?.id])

  // Drops a slow earlier response that would otherwise land after a faster
  // later one — easy to hit now that a header or facet click fires immediately.
  const beginRequest = useLatestRequest()

  // Load files
  const loadFiles = useCallback(async (refresh = false) => {
    const isCurrent = beginRequest()

    try {
      setLoading(true)
      const response = await filesApi.getAll(listParams)
      if (!isCurrent()) return
      setFiles(response.files)
      setBreadcrumb(response.breadcrumb)
      setTotalCount(response.pagination.total_count)

      // Clear selection when loading new files (e.g., navigating to different folder)
      if (!refresh) {
        setSelectedItems(new Set())
      }
    } catch (error: any) {
      if (!isCurrent()) return
      console.error('Error loading files:', error)

      if (error.response?.status === 401) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to access files",
          variant: "destructive",
        })
      } else {
        console.error('Full error object:', error)
        console.error('Error response data:', error.response?.data)

        const errorMessage = composeApiErrorMessage(error, {
          fallback: "Failed to load files",
          errorsEmptyFallback: "Failed to load files",
          blankFallback: "Failed to load files - please try again",
        })

        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        })
      }

      // Set empty defaults on error
      setFiles([])
      setBreadcrumb([])
      setTotalCount(0)
    } finally {
      // Guarded: a superseded request must not clear the spinner belonging to
      // the request that superseded it.
      if (isCurrent()) {
        setLoading(false)
        setInitialLoad(false)
      }
    }
  }, [listParams, beginRequest, toast])

  // Load files on mount and whenever the query changes.
  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete key when items are selected
      if (e.key === 'Delete' && selectedItems.size > 0) {
        e.preventDefault()
        setIsBulkDeleteOpen(true)
      }
      // Escape key to clear selection
      if (e.key === 'Escape' && selectedItems.size > 0) {
        e.preventDefault()
        setSelectedItems(new Set())
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedItems])

  // Handle folder navigation
  const navigateToFolder = (folder: FileItem | null) => {
    setCurrentFolder(folder)
    table.setPage(1)
  }

  const navigateToRoot = () => {
    setCurrentFolder(null)
    table.setPage(1)
  }

  const navigateToBreadcrumb = (folder: BreadcrumbItem) => {
    // Convert BreadcrumbItem to a minimal FileItem for navigation
    const fileItem: FileItem = {
      ...folder,
      path: '',
      has_children: true,
      can_be_deleted: true,
      created_at: '',
      updated_at: '',
      created_by: { id: '', name: '' }
    }
    setCurrentFolder(fileItem)
    table.setPage(1)
  }

  // File upload handling
  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) return

    try {
      for (const file of uploadFiles) {
        await filesApi.uploadFile({
          file,
          parent_id: currentFolder?.id
        })
      }

      toast({
        title: "Success",
        description: `${uploadFiles.length} file(s) uploaded successfully`,
      })

      setIsUploadDialogOpen(false)
      setUploadFiles([])
      setIsDragging(false)
      loadFiles(true)
    } catch (error: any) {
      console.error('Error uploading files:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to upload files",
        errorsEmptyFallback: "Upload failed",
        blankFallback: "Failed to upload files - please try again",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  // Folder creation
  const handleCreateFolder = async () => {
    if (!folderName.trim()) return

    try {
      await filesApi.createFolder({
        name: folderName.trim(),
        parent_id: currentFolder?.id
      })

      toast({
        title: "Success",
        description: "Folder created successfully",
      })

      setIsCreateFolderDialogOpen(false)
      setFolderName("")
      loadFiles(true)
    } catch (error: any) {
      console.error('Error creating folder:', error)
      handleApiError(error, toast, "Failed to create folder")
    }
  }

  // Edit file/folder
  const handleEdit = async () => {
    if (!selectedFile || !editName.trim()) return

    try {
      await filesApi.update(selectedFile.id, {
        name: editName.trim()
      })

      toast({
        title: "Success",
        description: `${selectedFile.is_folder ? 'Folder' : 'File'} updated successfully`,
      })

      setIsEditDialogOpen(false)
      setSelectedFile(null)
      setEditName("")
      loadFiles(true)
    } catch (error: any) {
      console.error('Error updating file:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to update",
        errorsEmptyFallback: "Update failed",
        blankFallback: "Failed to update - please check your input",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  // Delete file/folder
  const handleDelete = async () => {
    if (!selectedFile) return

    try {
      await filesApi.delete(selectedFile.id)

      toast({
        title: "Success",
        description: `${selectedFile.is_folder ? 'Folder' : 'File'} deleted successfully`,
      })

      setIsDeleteDialogOpen(false)
      setSelectedFile(null)
      loadFiles(true)
    } catch (error: any) {
      console.error('Error deleting file:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to delete",
        errorsEmptyFallback: "Delete failed",
        blankFallback: "Failed to delete - please try again",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  // Handle file/folder click
  const handleItemClick = (item: FileItem) => {
    if (item.is_folder) {
      navigateToFolder(item)
    } else {
      // Open file viewer for viewable files, otherwise show details
      setSelectedFile(item)
      if (isImageFile(item) || isPdfFile(item) || isVideoFile(item) || isAudioFile(item) ||
          isTextFile(item) || isExcelFile(item) || isCsvFile(item) || isDocumentFile(item)) {
        setIsFileViewerOpen(true)
      } else {
        setIsFileDetailsOpen(true)
      }
    }
  }

  // Force download (for use in dropdown menu)
  const handleDownload = async (file: FileItem) => {
    if (file.is_folder) return

    try {
      await filesApi.download(file.id)
      toast({
        title: "Success",
        description: "Download started",
      })
    } catch (error: any) {
      console.error('Error downloading file:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to download file",
        errorsEmptyFallback: "Download failed",
        blankFallback: "Failed to download file - please try again",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set dragging to false if we're leaving the drop zone completely
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      // Add to existing files if any, or replace
      setUploadFiles(prevFiles => [...prevFiles, ...droppedFiles])
    }
  }

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    setUploadFiles(prevFiles => [...prevFiles, ...selectedFiles])
  }

  // Remove file from upload list
  const removeUploadFile = (index: number) => {
    setUploadFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }

  // Bulk selection handlers (pure Set logic lives in crm/lib/selection.ts)
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => toggleId(prev, itemId))
  }

  const selectAllItems = () => {
    // If everything is already selected, clear; otherwise select all.
    setSelectedItems(allSelected(selectedItems.size, files.length) ? new Set() : collectIds(files, (file) => file.id))
  }

  const clearSelection = () => {
    setSelectedItems(new Set())
  }

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return

    try {
      const deletePromises = Array.from(selectedItems).map(itemId =>
        filesApi.delete(itemId)
      )

      await Promise.all(deletePromises)

      toast({
        title: "Success",
        description: `${selectedItems.size} item(s) deleted successfully`,
      })

      setIsBulkDeleteOpen(false)
      setSelectedItems(new Set())
      loadFiles(true)
    } catch (error: any) {
      console.error('Error deleting items:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to delete items",
        errorsEmptyFallback: "Delete failed",
        blankFallback: "Failed to delete items - please try again",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  // Move drag and drop handlers
  const handleItemDragStart = (e: React.DragEvent, item: FileItem) => {
    setDraggedItem(item)
    e.dataTransfer.setData('text/plain', item.id)
    e.dataTransfer.effectAllowed = 'move'

    // Add drag image with some styling
    const dragImage = document.createElement('div')
    dragImage.textContent = `${item.is_folder ? '📁' : '📄'} ${item.name}`
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    dragImage.style.backgroundColor = 'rgba(59, 130, 246, 0.8)'
    dragImage.style.color = 'white'
    dragImage.style.padding = '8px 12px'
    dragImage.style.borderRadius = '6px'
    dragImage.style.fontSize = '14px'
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 0, 0)

    setTimeout(() => document.body.removeChild(dragImage), 0)
  }

  const handleItemDragEnd = () => {
    setDraggedItem(null)
    setDropTarget(null)
  }

  const handleFolderDragEnter = (e: React.DragEvent, folderId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (draggedItem && draggedItem.id !== folderId) {
      setDropTarget(folderId)
    }
  }

  const handleFolderDragLeave = (e: React.DragEvent, folderId: string) => {
    e.preventDefault()
    e.stopPropagation()

    // Only clear drop target if we're really leaving this specific folder
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      // Only clear if we were targeting this specific folder
      if (dropTarget === folderId) {
        setDropTarget(null)
      }
    }
  }

  const handleFolderDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleFolderDrop = async (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDropTarget(null)

    if (!draggedItem || draggedItem.id === targetFolderId) {
      return
    }

    try {
      await filesApi.move(draggedItem.id, targetFolderId)

      toast({
        title: "Success",
        description: `${draggedItem.is_folder ? 'Folder' : 'File'} moved successfully`,
      })

      setDraggedItem(null)
      loadFiles(true)
    } catch (error: any) {
      console.error('Error moving item:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to move item",
        errorsEmptyFallback: "Move failed",
        blankFallback: "Failed to move item - please try again",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
      setDraggedItem(null)
    }
  }

  // Root drop handlers
  // - For external files from OS: Allow upload to current folder
  // - For internal items: Don't allow drop to same folder (files are already here)
  // - Only show green highlights on subfolders for internal drags
  const handleRootDragEnter = (e: React.DragEvent) => {
    e.preventDefault()

    // Check if this is an external drag from OS (has files) or internal drag (our own items)
    const hasExternalFiles = e.dataTransfer.types.includes('Files')

    // Only set root target if:
    // 1. We're not already over a specific folder AND
    // 2. Either it's an external drag from OS OR there's no internal draggedItem
    if (!dropTarget && (hasExternalFiles || !draggedItem)) {
      setDropTarget('root')
    }
  }

  const handleRootDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    // Check if we're really leaving the drop zone
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX
    const y = e.clientY

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      if (dropTarget === 'root') {
        setDropTarget(null)
      }
    }
  }

  const handleRootDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDropTarget(null)

    // Check if this is an external file drop from OS
    const droppedFiles = Array.from(e.dataTransfer.files)

    if (droppedFiles.length > 0) {
      // Handle external file upload from OS
      try {
        for (const file of droppedFiles) {
          await filesApi.uploadFile({
            file,
            parent_id: currentFolder?.id
          })
        }

        toast({
          title: "Success",
          description: `${droppedFiles.length} file(s) uploaded successfully`,
        })

        loadFiles(true)
      } catch (error: any) {
        console.error('Error uploading files:', error)
        console.error('Error response data:', error.response?.data)

        const errorMessage = composeApiErrorMessage(error, {
          fallback: "Failed to upload files",
          errorsEmptyFallback: "Upload failed",
          blankFallback: "Failed to upload files - please try again",
        })

        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        })
      }
      return
    }

    // Handle internal item move (this shouldn't happen with new logic, but keeping for safety)
    if (!draggedItem) {
      return
    }

    // Move to current folder (or root if no current folder)
    const targetFolderId = currentFolder?.id || null

    try {
      await filesApi.move(draggedItem.id, targetFolderId)

      toast({
        title: "Success",
        description: `${draggedItem.is_folder ? 'Folder' : 'File'} moved successfully`,
      })

      setDraggedItem(null)
      loadFiles(true)
    } catch (error: any) {
      console.error('Error moving item:', error)
      console.error('Error response data:', error.response?.data)

      const errorMessage = composeApiErrorMessage(error, {
        fallback: "Failed to move item",
        errorsEmptyFallback: "Move failed",
        blankFallback: "Failed to move item - please try again",
      })

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
      setDraggedItem(null)
    }
  }

  // Get file icon
  // Icon colors are decorative file-type accents (baked to sRGB hex from the
  // former Tailwind text-* classes during the MUI 5 migration), not semantic
  // palette roles.
  // Format date
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, justifyContent: { sm: "space-between" } }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>File Manager</Typography>
          <Typography sx={{ color: "text.secondary" }}>Manage your files and folders with unlimited nesting</Typography>
        </Box>
        <Box sx={{ mt: { xs: 2, sm: 0 }, display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Upload size={16} />}
            onClick={() => setIsUploadDialogOpen(true)}
          >
            Upload Files
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FolderPlus size={16} />}
            onClick={() => setIsCreateFolderDialogOpen(true)}
          >
            New Folder
          </Button>
        </Box>
      </Box>

      {/* File Manager Table */}
      <Card>
        <CardHeader
          title={
            <>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, justifyContent: { sm: "space-between" }, gap: 2 }}>
                <Box>
                  <Typography component="span" sx={{ fontWeight: 600 }}>Files and Folders</Typography>
                  {/* Breadcrumb */}
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1, fontSize: "0.875rem", color: "text.secondary" }}>
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      onClick={navigateToRoot}
                      startIcon={<Home size={12} />}
                      sx={{ minWidth: 0, p: 0.5 }}
                    >
                      Root
                    </Button>

                    {breadcrumb.map((folder) => (
                      <Box key={folder.id} sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ mx: 0.5, display: "flex" }}><ChevronRight size={12} /></Box>
                        <Button
                          variant="text"
                          color="inherit"
                          size="small"
                          onClick={() => navigateToBreadcrumb(folder)}
                          sx={{ minWidth: 0, p: 0.5 }}
                        >
                          {folder.name}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* The search box is WRAPPED, not replaced — same field, same
                    500ms debounce; the two facets simply sit beside it. The
                    "Sort" dropdown this used to host is GONE: every column
                    header sorts now, so a second, separate sort control would
                    be a way to disagree with the arrows. */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TableFilterBar
                    facetDefs={facetDefs}
                    values={table.facets}
                    onChange={table.setFacet}
                    onClearAll={table.clearFacets}
                    searchSlot={
                      <TextField
                        size="small"
                        placeholder="Search files and folders..."
                        value={search.input}
                        onChange={(e) => search.setInput(e.target.value)}
                        autoComplete="off"
                        sx={{ width: { xs: "100%", sm: 300 } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search size={16} color={search.isSearching ? "#2563eb" : undefined} />
                            </InputAdornment>
                          ),
                          ...(search.isSearching ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography sx={{ fontSize: "0.875rem", color: "primary.main" }}>Searching...</Typography>
                              </InputAdornment>
                            ),
                          } : {}),
                        }}
                      />
                    }
                  />
                </Box>
              </Box>

              {/* Selection Actions - Only shown when items are selected */}
              {selectedItems.size > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 2, borderTop: 1, borderColor: "divider", mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", fontSize: "0.875rem", color: "text.secondary" }}>
                    {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={clearSelection}
                      size="small"
                    >
                      Clear Selection
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setIsBulkDeleteOpen(true)}
                      size="small"
                      startIcon={<Trash2 size={16} />}
                    >
                      Delete Selected
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          }
        />
        <CardContent
          sx={{ ...(dropTarget === 'root' ? { bgcolor: "action.hover" } : {}) }}
          onDragEnter={handleRootDragEnter}
          onDragLeave={handleRootDragLeave}
          onDragOver={(e) => {
            e.preventDefault()
            // Set appropriate drop effect based on drag type
            const hasExternalFiles = e.dataTransfer.types.includes('Files')

            if (hasExternalFiles) {
              // External files from OS - allow upload
              e.dataTransfer.dropEffect = 'copy'
            } else {
              // Internal item drag - allow move (folders will handle their own drop logic)
              e.dataTransfer.dropEffect = 'move'
            }
          }}
          onDrop={handleRootDrop}
        >
          {loading || initialLoad ? (
            <PageLoader />
          ) : files.length === 0 ? (
            // The empty area is ALSO the upload drop target, so the dashed
            // border and the "drop here" copy have to survive: while something
            // is being dragged over it, that instruction is the only message
            // worth showing. Every other empty case goes through the shared
            // TableEmptyState, which names whichever narrowing actually emptied
            // the list — filtering to nothing used to read "This folder is
            // empty", i.e. it told the operator their folder had no files when
            // it has plenty and one facet was on.
            <Box sx={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              ...(dropTarget === 'root'
                ? { py: 6, textAlign: "center", border: 2, borderStyle: "dashed", borderColor: "divider", borderRadius: 1, mx: 4, my: 4 }
                : {}),
            }}>
              {dropTarget === 'root' ? (
                <>
                  <Box sx={{ mx: "auto", display: "flex", width: 64, height: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.primary.main, 0.12), mb: 2 }}>
                    <Folder size={32} color="#2563eb" />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
                    This folder is empty
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 3, maxWidth: "24rem" }}>
                    Drop files here to upload to {currentFolder ? `"${currentFolder.name}"` : 'root folder'}
                  </Typography>
                </>
              ) : (
                <TableEmptyState
                  entityNoun="files"
                  hasSearch={search.input.trim().length > 0}
                  activeFilterCount={table.activeFilterCount}
                  onClearFilters={table.clearFacets}
                  emptyTitle="This folder is empty"
                  emptyBody="Get started by uploading files or creating folders to organize your content."
                />
              )}
            </Box>
          ) : (
            <Box sx={{
              ...(dropTarget === 'root' ? { border: 2, borderStyle: "dashed", borderColor: "divider", borderRadius: 1, m: 1, p: 1 } : {}),
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                  <TableCell sx={{ width: 50 }}>
                    <Checkbox
                      checked={allSelected(selectedItems.size, files.length)}
                      indeterminate={isIndeterminate(selectedItems.size, files.length)}
                      onChange={selectAllItems}
                      aria-label="Select all files"
                    />
                  </TableCell>
                  {/* The four DATA columns, in `FILES_TABLE` order — which is
                      the order the body cells below are written in, so the two
                      must be edited together. The checkbox and actions cells
                      stay plain <TableCell>s: neither maps to a server column,
                      so neither can sort. */}
                  {columns.map((col) => (
                    <SortableHeader
                      key={col.key}
                      column={col.key}
                      label={col.label}
                      sort={table.sort}
                      dir={table.dir}
                      onSort={table.toggleSort}
                      {...(col.align ? { align: col.align } : {})}
                    />
                  ))}
                  <TableCell sx={{ width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => {
                  const isDropTarget = dropTarget === file.id && file.is_folder && Boolean(draggedItem)
                  const isFolderHoverTarget = file.is_folder && draggedItem && draggedItem.id !== file.id
                  return (
                  <TableRow
                    key={file.id}
                    sx={{
                      cursor: "grab",
                      "&:active": { cursor: "grabbing" },
                      ...(draggedItem?.id === file.id ? { opacity: 0.5 } : {}),
                      ...(selectedItems.has(file.id)
                        ? { bgcolor: "#eff6ff", borderLeft: 4, borderColor: "#3b82f6" }
                        : {}),
                      ...(isDropTarget
                        ? { bgcolor: "#dcfce7", borderLeft: 4, borderColor: "#22c55e", boxShadow: "0 0 0 2px #bbf7d0" }
                        : {}),
                      "&:hover": isFolderHoverTarget
                        ? { bgcolor: "#f0fdf4", borderLeft: "2px solid #86efac" }
                        : { bgcolor: "action.hover" },
                    }}
                    draggable={true}
                    onDragStart={(e) => handleItemDragStart(e, file)}
                    onDragEnd={handleItemDragEnd}
                    {...(file.is_folder ? {
                      onDragEnter: (e: React.DragEvent) => handleFolderDragEnter(e, file.id),
                      onDragLeave: (e: React.DragEvent) => handleFolderDragLeave(e, file.id),
                      onDragOver: handleFolderDragOver,
                      onDrop: (e: React.DragEvent) => handleFolderDrop(e, file.id)
                    } : {})}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedItems.has(file.id)}
                        onChange={() => toggleItemSelection(file.id)}
                        aria-label={`Select ${file.name}`}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleItemClick(file)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FileTypeIcon file={file} />
                        <Box sx={{ fontWeight: 500, color: "text.primary" }}>{file.name}</Box>
                      </Box>
                    </TableCell>
                    {/* align right, to sit under the right-aligned Size header
                        `FILES_TABLE` declares — a right-aligned header over a
                        left-aligned column reads as a layout bug. */}
                    <TableCell
                      align="right"
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleItemClick(file)}
                    >
                      <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                        {file.is_folder ? '—' : (file.file_size_formatted || '—')}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleItemClick(file)}
                    >
                      <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                        {formatFileDate(file.updated_at)}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleItemClick(file)}
                    >
                      <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                        {file.created_by?.name || 'Unknown User'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        sx={{ width: 32, height: 32 }}
                        aria-label="Open menu"
                        onClick={(e) => { e.stopPropagation(); setRowMenuAnchor({ el: e.currentTarget, id: file.id }) }}
                      >
                        <MoreHorizontal size={16} />
                      </IconButton>
                      <Menu
                        anchorEl={rowMenuAnchor?.id === file.id ? rowMenuAnchor.el : null}
                        open={rowMenuAnchor?.id === file.id}
                        onClose={() => setRowMenuAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        {!file.is_folder && (isImageFile(file) || isPdfFile(file) || isVideoFile(file) || isAudioFile(file) ||
                          isTextFile(file) || isExcelFile(file) || isCsvFile(file) || isDocumentFile(file)) && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setRowMenuAnchor(null)
                              setSelectedFile(file)
                              setIsFileViewerOpen(true)
                            }}
                          >
                            <ListItemIcon><Eye size={16} /></ListItemIcon>
                            Open File
                          </MenuItem>
                        )}
                        {!file.is_folder && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setRowMenuAnchor(null)
                              setSelectedFile(file)
                              setIsFileDetailsOpen(true)
                            }}
                          >
                            <ListItemIcon><FileText size={16} /></ListItemIcon>
                            View Details
                          </MenuItem>
                        )}
                        {!file.is_folder && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setRowMenuAnchor(null)
                              handleDownload(file)
                            }}
                          >
                            <ListItemIcon><Download size={16} /></ListItemIcon>
                            Download
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            setRowMenuAnchor(null)
                            setSelectedFile(file)
                            setEditName(file.name)
                            setIsEditDialogOpen(true)
                          }}
                        >
                          <ListItemIcon><Edit size={16} /></ListItemIcon>
                          Edit
                        </MenuItem>
                        {file.can_be_deleted && (
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setRowMenuAnchor(null)
                              setSelectedFile(file)
                              setIsDeleteDialogOpen(true)
                            }}
                            sx={{ color: "error.main" }}
                          >
                            <ListItemIcon sx={{ color: "error.main" }}><Trash2 size={16} /></ListItemIcon>
                            Delete
                          </MenuItem>
                        )}
                      </Menu>
                    </TableCell>
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </Box>
          )}

          {/* Pagination */}
          {Math.ceil(totalCount / perPage) > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 2 }}>
              <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                Showing {((table.page - 1) * perPage) + 1} to{' '}
                {Math.min(table.page * perPage, totalCount)} of{' '}
                {totalCount} files
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => table.setPage(table.page - 1)}
                  disabled={table.page <= 1}
                >
                  Previous
                </Button>
                <Box component="span" sx={{ fontSize: "0.875rem" }}>
                  Page {table.page} of {Math.ceil(totalCount / perPage)}
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => table.setPage(table.page + 1)}
                  disabled={table.page >= Math.ceil(totalCount / perPage)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Files Dialog */}
      <UploadFilesDialog
        open={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        currentFolder={currentFolder}
        uploadFiles={uploadFiles}
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onFileInputChange={handleFileInputChange}
        onRemoveFile={removeUploadFile}
        onUpload={handleFileUpload}
        onCancel={() => {
          setIsUploadDialogOpen(false)
          setUploadFiles([])
          setIsDragging(false)
        }}
      />

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={isCreateFolderDialogOpen}
        onOpenChange={setIsCreateFolderDialogOpen}
        currentFolder={currentFolder}
        folderName={folderName}
        onFolderNameChange={setFolderName}
        onCreate={handleCreateFolder}
      />

      {/* Edit Dialog */}
      <EditFileDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        selectedFile={selectedFile}
        editName={editName}
        onEditNameChange={setEditName}
        onSave={handleEdit}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title={`Delete ${selectedFile?.is_folder ? 'Folder' : 'File'}?`}
        description={
          <>
            Are you sure you want to delete "{selectedFile?.name}"? This action cannot be undone.
            {selectedFile?.is_folder && " All contents in this folder will also be deleted."}
          </>
        }
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />

      {/* File Details Dialog */}
      <FileDetailsDialog
        open={isFileDetailsOpen}
        onOpenChange={setIsFileDetailsOpen}
        selectedFile={selectedFile}
        onDownload={handleDownload}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        title={`Delete ${selectedItems.size} Item${selectedItems.size > 1 ? 's' : ''}?`}
        description={
          <>
            Are you sure you want to delete {selectedItems.size} selected item{selectedItems.size > 1 ? 's' : ''}? This action cannot be undone.
            Any folders will be deleted along with their contents.
          </>
        }
        confirmLabel={`Delete ${selectedItems.size} Item${selectedItems.size > 1 ? 's' : ''}`}
        onConfirm={handleBulkDelete}
      />

      {/* File Viewer Dialog */}
      <PageFileViewerDialog
        open={isFileViewerOpen}
        onOpenChange={(open) => {
          setIsFileViewerOpen(open)
          // Clear errors when dialog closes
          if (!open) {
            clearFileViewerErrors()
          }
        }}
        selectedFile={selectedFile}
        onError={(error) => {
          toast({
            title: "File Viewer Error",
            description: error,
            variant: "destructive",
          })
        }}
        onDownload={handleDownload}
        onShowDetails={(file) => {
          setSelectedFile(file)
          setIsFileViewerOpen(false)
          setIsFileDetailsOpen(true)
        }}
      />
    </Box>
  )
}

export default withFileManagerAccess(FileManagerPage)
